import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@material-ui/core";
import { Alert } from "@mui/material";
import ApproverNameField from "features/activity/components/ApproverNameField/ApproverNameField";
import Authorize from "components/Authorize";
import { hasPermission } from "components/Authorize/authorizeUtil";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { permissions } from "components/Authorize/permissions/permissions";
import CustomInput from "components/CustomInput";
import DateTimeField from "components/DateTimePicker/DateTimePicker";
import DropdownFilter from "components/DropdownFilter";
import NotificationProvider from "components/NotificationProvider";
import { activityStatuses, activityStatusReasons } from "features/activity/activityConsts";
import { useGetActivityStatusReasonsQuery, useGetAllActivitiesQuery, useMoveToStatusMutation } from "features/activity/activitySlice";
import ActivityStatusAliasView from "features/activityStatusAlias";
import { NoteContent } from "features/notes/NoteContent";
import { selectIsClient } from "features/user/userSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { orderBy } from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { activityTableStyle } from "./style";
import VendorDetachedEmailDialog from "features/vms/vendorDirectory/vendorDetachedTemplate/VendorDetachedEmailDialog";

const BulkUpdateActivityDialog = ({
    selectedRows, open, setOpen, activity = null, activityLogs = [], onClear
}) => {

    const classes = activityTableStyle();
    const [emailDraft, setEmailDraft] = useState(null);
    const [emailActivityLogId, setEmailActivityLogId] = useState(null);
    let { data: activities, error: activityError, isLoading: isLoadingActivity } = useGetAllActivitiesQuery();
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();
    let { data: statusReasons, error: statusReasonsError, isLoading: isLoadingStatusReasons } = useGetActivityStatusReasonsQuery();
    vendors = vendors || [];
    statusReasons = statusReasons || [];
    vendors = orderBy(vendors, 'name');

    //TODO if all activity item have noCharge marked as true we might want to also allow completing activities
    let statusValues = ((!hasPermission(permissions.ACTIVITIES.MANAGE_PENDING_INVOICE.key)) ? activities?.filter(a => a.id !== activityStatuses.Completed && a.id !== activityStatuses.CompleteBilling && a.id !== activityStatuses.CompletedAndPaid) : activities) ?? [];

    const [updateStatus, { isLoading }] = useMoveToStatusMutation();

    const [activityToEdit, setActivityToEdit] = useState(activity);
    const [note, setNote] = useState();
    const [approverName, setApproverName] = useState('');

    useEffect(() => {
        setActivityToEdit(activity || {});
        setApproverName('');
    }, [activity]);

    const isClient = useSelector(selectIsClient);

    const selectedActivityLogs = activityLogs.filter(a => selectedRows?.includes(a.id));
    const targetStatus = parseInt(activityToEdit?.status);
    const isL1Transition = targetStatus == activityStatuses.PendingApproval
        && selectedActivityLogs.some(a => a.activityStatus == activityStatuses.PendingClientDecision);
    const isL2Transition = targetStatus == activityStatuses.PricingApprovedByClient
        && selectedActivityLogs.some(a => a.activityStatus == activityStatuses.PendingApproval);
    const requiresApproverName = (isL1Transition || isL2Transition) && !isClient;

    const selectedClientAccountIds = [...new Set(selectedActivityLogs.map(a => a.clientAccountId))];
    const approverClientAccountId = selectedClientAccountIds.length == 1 ? selectedClientAccountIds[0] : undefined;

    const lockCost = activityToEdit?.activityId == activityStatuses.Completed || activityToEdit?.activityId == activityStatuses.CompleteBilling || activityToEdit?.activityId == activityStatuses.CompletedAndPaid;
    const lockCharge = activityToEdit?.activityId == activityStatuses.CompleteBilling || activityToEdit?.activityId == activityStatuses.CompletedAndPaid;

    const onChangeField = (key, val) => {
        setActivityToEdit({ ...activityToEdit, [key]: val });
    }

    const handleNoteChange = (field, value) => {
        setNote({ ...note, [field]: value });
    }

    const onSave = async () => {
        const res = await updateStatus({
            activityLogIds: selectedRows,
            moveToStatus: activityToEdit?.status,
            reasonId: activityToEdit?.reasonId,
            vendorId: activityToEdit?.vendorId,
            cost: activityToEdit?.cost,
            charge: activityToEdit?.revenue,
            requestedCompletionDate: activityToEdit?.requestedCompletionDate,
            offSiteWork: activityToEdit?.offSiteWork,
            note,
            noCharge: activityToEdit?.noCharge,
            repairOrder: activityToEdit?.repairOrder,
            approverName: (isL1Transition || isL2Transition) ? approverName : undefined
        });
        if (res?.error) {
            NotificationProvider.error(`An error occured while updating activities, please try again.`);
            return;
        } else {
            NotificationProvider.success('Successfully updated the activities');
            setOpen(false);
            if (res?.data?.emailDraft) {
                setEmailActivityLogId(selectedRows?.[0]);
                setEmailDraft(res.data.emailDraft);
            } else {
                onClear();
            }
        }
    };

    return (
        <>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth={true} >
                <DialogTitle>{`Update Activit${selectedRows?.length == 1 ? "y" : "ies"}`}</DialogTitle>
                <DialogContent>
                    <div className={classes.input}>
                        <ActivityStatusAliasView
                            activityIds={selectedRows}
                            readonly={false}
                            value={activityToEdit?.status}
                            onChange={(val) => onChangeField('status', val)}
                            dropdownValues={statusValues}
                        />
                    </div>
                    {activityToEdit?.status == activityStatuses.Exception && <div className={classes.input}>
                        {!activityToEdit?.reasonId && <div style={{ paddingBottom: '1em' }}><Alert severity="warning">Please select a reason for the exception</Alert></div>}
                        <CustomInput
                            id="reasonId"
                            label="Reason"
                            elementType="dropdown"
                            values={statusReasons}
                            value={activityToEdit?.reasonId}
                            required={activityToEdit?.status == activityStatuses.Exception}
                            showEmpty={true}
                            onChange={val => onChangeField('reasonId', val)}
                        />
                    </div>}
                    <div className={classes.input}>
                        <DropdownFilter
                            id="ddlVendorId"
                            title="Vendor"
                            elementType="dropdown"
                            values={vendors ?? []}
                            value={activityToEdit?.vendorId}

                            classStyles={classes.dropdownStyle}
                            showEmpty={true}
                            withTypeAhead
                            shrink={true}
                            onSelect={(val) => onChangeField('vendorId', val?.id)}
                        />
                    </div>
                    <div className={classes.input}>
                        <DateTimeField
                            label='Due Date'
                            value={activityToEdit?.requestedCompletionDate ?? null}
                            onChange={(e) => onChangeField('requestedCompletionDate', e)}
                        />
                    </div>
                    <Authorize profile={permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS}>
                        <div className={classes.input}>
                            <CustomInput
                                id="cost"
                                label="Internal Cost"
                                value={activityToEdit?.cost}
                                elementType="input"
                                onChange={(val) => onChangeField('cost', val != "" ? val : 0)}
                                required={!lockCost}
                                disabled={lockCost}
                            />
                        </div>
                    </Authorize>
                    <div className={classes.input}>
                        <CustomInput
                            id="charge"
                            label="Charge"
                            value={activityToEdit?.revenue}
                            elementType="input"
                            onChange={(val) => onChangeField('revenue', val != "" ? val : 0)}
                            disabled={lockCharge}
                        />
                    </div>
                    {requiresApproverName && <div className={classes.input}>
                        <ApproverNameField value={approverName} onChange={setApproverName} clientAccountId={approverClientAccountId} />
                    </div>}
                    {isL2Transition && <div className={classes.input}>
                        <CustomInput
                            id="approvalAmount"
                            label="Approval Amount (Charge)"
                            value={activityToEdit?.revenue ?? ''}
                            elementType="input"
                            disabled={true}
                        />
                    </div>}
                    <div className={classes.input}>
                        <CustomInput
                            id="repairOrder"
                            label="Repair Order"
                            elementType="text"
                            value={activityToEdit?.repairOrder}
                            onChange={(val) => onChangeField('repairOrder', val)}
                        />
                    </div>
                    <div className={classes.input}>
                        {(activityToEdit?.reasonId == activityStatusReasons.Other && !(note?.content?.replace(/<[^>]+>/g, '')?.trim())) ?
                            <Alert style={{ marginBottom: '1em' }} severity="error">Note is required</Alert> : <div>Note</div>}
                        <NoteContent content={note?.content} viewByClient={isClient ? true : note?.viewByClient} onChange={handleNoteChange} />
                    </div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={activityToEdit?.offSiteWork}
                                onChange={(e, value) => onChangeField('offSiteWork', value)}
                            />
                        }
                        label={<div>Off-Site Work</div>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={activityToEdit?.noCharge}
                                onChange={(e, value) => onChangeField('noCharge', value)}
                            />
                        }
                        label={<div>No Charge Client</div>}
                    />
                    <DialogActions >
                        <Button
                            onClick={onSave}
                            color="primary"
                            variant="outlined"
                            disabled={isLoading || (requiresApproverName && !approverName?.trim()) || (activityToEdit?.status == activityStatuses.Exception
                                && (!activityToEdit?.reasonId || (activityToEdit?.reasonId == activityStatusReasons.Other && !(note?.content?.replace(/<[^>]+>/g, '')?.trim()))))}>
                            Save
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
            <VendorDetachedEmailDialog
                open={!!emailDraft}
                onClose={() => { setEmailDraft(null); onClear?.(); }}
                emailDraft={emailDraft}
                activityLogId={emailActivityLogId}
            />
        </>
    )
}

export default BulkUpdateActivityDialog;